import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useTheme, type Theme } from "@mui/material/styles";
import { apiRequest, pageQuery, type Envelope } from "../api/client";
import { useSession } from "../auth/session";
import { readClaims } from "../auth/token";
import { useToast } from "../ui/toast";
import { Icon } from "../ui/Icon";
import {
  EGM_PAGE_SIZE,
  markSkippedThisSession,
  needsSelection,
  saveBody,
  saveSucceeded,
  sectionsFromEgmPage,
  sessionFingerprint,
  storeSections,
  wasSkippedThisSession,
} from "./floor-sections";

// PORT OF `floor-section-prompt` (ISSUES #71 AND #98): THE REQUIRED "WHICH FLOOR
// SECTION ARE YOU WORKING?" DIALOG. IT LIVES IN THE APP SHELL, NOT THE LOGIN PAGE,
// SO IT COVERS BOTH WAYS IN: THE LOGIN FORM AND THE NATIVE SHELLS' `?t=` HANDOFF.
//
// "REQUIRED" MUST NEVER BECOME "INESCAPABLE". THE SECTION LIST IS FETCHED BEFORE
// THE DIALOG OPENS, AND THE DIALOG ONLY OPENS WHEN THE HOST CAN ANSWER IT:
//   SECTIONS FOUND      -> OPEN; NO WAY OUT BUT PICKING
//   NO SECTIONS AT ALL  -> NEVER OPEN (NOTHING TO ASK)
//   LOOKUP FAILED       -> OPEN WITH RETRY *AND* AN EXPLICIT WAY OUT

// MANAGERS ARE NOT ASSIGNED TO A SECTION. BOTH MANAGER GROUPS COUNT HERE - UNLIKE
// THE DASHBOARD'S COSMETIC CHECK, READING A `PILLARS-MANAGER` USER AS A HOST WOULD
// TRAP THEM BEHIND A DIALOG WITH NO CLOSE BUTTON.
const MANAGER_GROUPS = ["PILLARS-HOST-MANAGER", "PILLARS-MANAGER"];

// THE CHIPS ARE FILLED IN BOTH STATES AND DIFFER ONLY BY SHADE, AS THE PILLARS
// THEMES DRAW `.p-selectbutton`: `--primary-500` UNSELECTED, `--primary-600` ON
// HOVER, `--primary-700` SELECTED - NEVER OUTLINED. SIZES ARE PRIMENG'S `.p-button`
// AT ANGULAR'S 14PX ROOT (0.5REM 0.75REM PADDING, 4PX RADIUS).
const SHADES = {
  light: { 500: "#009aff", 600: "#0069d9", 700: "#0056b3" },
  dark: { 500: "#009aff", 600: "#008be6", 700: "#007bcc" },
};
const chip = (theme: Theme) => ({
  color: "#ffffff",
  bgcolor: SHADES.light[500],
  border: `1px solid ${SHADES.light[500]}`,
  borderRadius: "4px",
  p: "7px 10.5px",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: 1.2,
  textTransform: "none" as const,
  transition: "background-color 0.15s, border-color 0.15s",
  "&:hover": { bgcolor: SHADES.light[600], borderColor: SHADES.light[600] },
  "&.Mui-selected, &.Mui-selected:hover": { color: "#ffffff", bgcolor: SHADES.light[700], borderColor: SHADES.light[700] },
  "&.Mui-disabled": { color: "#ffffff", opacity: 0.6 },
  ...theme.applyStyles("dark", {
    "&:hover": { bgcolor: SHADES.dark[600], borderColor: SHADES.dark[600] },
    "&.Mui-selected, &.Mui-selected:hover": { bgcolor: SHADES.dark[700], borderColor: SHADES.dark[700] },
  }),
});

export function FloorSectionPrompt() {
  const theme = useTheme();
  const session = useSession();
  const toast = useToast();
  const userId = readClaims()?.userId ?? null;
  const fingerprint = sessionFingerprint();

  // WHICH SESSION THIS PROMPT HAS FINISHED WITH (SAVED OR SKIPPED), SO A NEW
  // `?t=` SIGN-IN IN THE SAME TAB IS ASKED AGAIN
  const [doneFor, setDoneFor] = useState<string | null>(null);

  // ROLES MUST HAVE LOADED: A FAILED ROLES REQUEST READS EVERY ROLE AS ABSENT,
  // WHICH WOULD PROMPT A MANAGER. IN THAT CASE THE PROMPT IS SKIPPED, NOT GUESSED.
  const eligible =
    session.ready &&
    !MANAGER_GROUPS.some((g) => session.hasRole(g)) &&
    userId !== null &&
    doneFor !== fingerprint &&
    needsSelection(userId) &&
    !wasSkippedThisSession();

  const options = useQuery({
    // THE LOOKUP SENDS NO SITE IDS, SO THE LIST IS THE WHOLE FLOOR FOR EVERYONE -
    // NOTHING USER-SPECIFIC TO KEY ON. A FAILURE IS NEVER CACHED AS AN ANSWER.
    queryKey: ["floor-section-options"],
    queryFn: ({ signal }) =>
      apiRequest<Envelope<unknown>>(
        `/ht/paged-egms?${pageQuery({ page: 1, pageSize: EGM_PAGE_SIZE, orderBy: "section", orderDir: "asc" })}`,
        { method: "POST", body: [], signal },
      ).then((r) => sectionsFromEgmPage(r.data)),
    enabled: eligible,
    staleTime: Infinity,
    retry: false,
  });

  const [selected, setSelected] = useState<string[]>([]);
  const save = useMutation({
    mutationFn: async (sections: string[]) => {
      const resp = await apiRequest<unknown>("/crm/update-floor-sections", { method: "POST", body: saveBody(userId ?? 0, sections) });
      // THE ENVELOPE CAN REPORT A FAILURE ON AN HTTP 200 - NOTHING WAS WRITTEN
      if (!saveSucceeded(resp)) throw new Error("Floor sections were not saved");
      return sections;
    },
  });

  const sectionList = options.data ?? [];
  const loadFailed = options.isError;
  // A RETRY AFTER A FAILURE KEEPS THE ERROR STATUS WHILE IT FETCHES, SO THE DIALOG STAYS UP
  const open = eligible && ((options.isSuccess && sectionList.length > 0) || loadFailed);
  if (!open) return null;

  // DERIVED, NOT STORED: TICKING EVERY SECTION BY HAND READS AS "ALL" (#98).
  // MATCHED PER OPTION SO TWO DISAGREEING LISTS CAN NEVER READ AS A FULL SET.
  const allSelected = sectionList.length > 0 && sectionList.every((s) => selected.includes(s));
  const busy = save.isPending;
  const canSave = !busy && !options.isFetching && selected.length > 0;

  const startShift = () => {
    if (!canSave || userId === null) return;
    // PIN WHAT IS SENT: THE ECHO AND THE TOAST MUST DESCRIBE THE SELECTION THE
    // SERVER RECEIVED, NOT WHATEVER IS SELECTED WHEN THE RESPONSE LANDS
    const sections = [...selected];
    save.mutate(sections, {
      onSuccess: () => {
        // ECHOED ONLY AFTER A CONFIRMED SAVE, SO A FAILED ONE ASKS AGAIN
        storeSections(userId, sections);
        setDoneFor(fingerprint);
        toast("success", "Floor Sections", `You are signed in to ${sections.join(", ")}.`);
      },
      onError: () => toast("error", "Floor Sections", "Could not save your floor sections, please try again."),
    });
  };

  const continueWithout = () => {
    // ONLY OFFERED WHEN THE PROMPT CANNOT BE ANSWERED. NOTHING IS SAVED, SO A
    // RELOAD OR NEW SIGN-IN ASKS AGAIN; NAVIGATING DOES NOT.
    markSkippedThisSession();
    setDoneFor(fingerprint);
  };

  return (
    // NO CLOSE BUTTON. THE NO-OP `onClose` REFUSES ESCAPE AND BACKDROP CLICKS ALIKE:
    // THE DIALOG ONLY CLOSES WHEN `open` TURNS FALSE, WHICH ONLY A SAVE OR A SKIP DOES
    <Dialog open fullWidth maxWidth="xs" onClose={() => {}} aria-labelledby="floor-section-title">
      <DialogTitle id="floor-section-title">Which floor section are you working?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select every section you are covering today. Your notifications are limited to players in these sections.
        </Typography>

        {options.isFetching ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : loadFailed ? (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
              The floor sections could not be loaded.
            </Alert>
            <Button variant="outlined" onClick={() => void options.refetch()}>
              Try Again
            </Button>
          </Box>
        ) : (
          <>
            {/* A BUTTON, NOT A CHECKBOX: SAME TOUCH TARGET AS THE SECTIONS, TAPPED ON THE FLOOR */}
            {/* A RULE SEPARATES "ALL" FROM THE SECTIONS SO IT DOES NOT READ AS ONE OF THEM */}
            <Box sx={{ mb: "10.5px", pb: "10.5px", borderBottom: 1, borderColor: "divider" }}>
              <ToggleButton
                value="all"
                selected={allSelected}
                aria-pressed={allSelected}
                disabled={busy}
                onChange={() => setSelected(allSelected ? [] : [...sectionList])}
                sx={chip(theme)}
              >
                All
              </ToggleButton>
            </Box>
            <ToggleButtonGroup
              value={selected}
              onChange={(_, v: string[]) => setSelected(v)}
              disabled={busy}
              aria-label="Floor sections"
              // SEPARATE, WRAPPING CHIPS WITH A 0.5REM GAP, NOT ONE JOINED BUTTON SET
              sx={{ display: "flex", flexWrap: "wrap", gap: "7px", "& .MuiToggleButtonGroup-grouped": { ...chip(theme), m: 0, ml: 0 } }}
            >
              {sectionList.map((s) => (
                <ToggleButton key={s} value={s}>
                  {s}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {loadFailed && !options.isFetching && <Button onClick={continueWithout}>Continue Without Sections</Button>}
        {!loadFailed && (
          <Button variant="contained" onClick={startShift} disabled={!canSave} loading={busy} startIcon={<Icon name="check" size={20} />}>
            Start My Shift
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
